import { useSelector } from "react-redux"
import { useTranslation } from "react-i18next"

import { selectIsReviewing } from "src/store/game"
import { selectGameConfig } from "src/store/common"

import ModalService from "src/modals"
import { races, raceUnits, unitIcons } from "src/assets/images"
import { HostIcon } from "src/components/icons"
import { describeBonus, raceNames, specialUnitFor, unitName } from "src/utils/races"
import { crownStandings, readiness } from "src/utils/crowns"
import { isSurvival } from "src/utils/gameModes"
import { useScrollEdges } from "src/utils/scrollEdges"
import { zoneNames } from "src/utils/battlefield"
import { seatIconFor } from "src/utils/cosmetics"

import "./PlayerStatuses.css"

/* One flat roster instead of two team columns: at a glance you want "who's left to ready up",
   not which side they're on — the row itself (border color, name tint) already says that.

   **A seat is the class plate, with three facts written on it** (owner, 2026-09-10). It used to be
   a 28px stamp beside a line reading "tú · anfitrión · humano · guerrero · 4 ciudades", which is
   five answers to questions nobody was asking of a roster and none of them the picture. Now the
   art is the card, at the plate's own proportion — pressed, it opens the showcase, which is where
   "what does that class actually do" is answered properly — and the name, the mark of whoever
   holds the clock and `👑👑👑 · 4 bases` sit in its lower third. The only thing *under* the
   picture is a bot's difficulty.

   Everything taken off the line is still on screen or still derivable: the side is the plate's
   border and the name's tint, the class is the face, elimination is three dark crowns over a grey
   plate, and "which one is me" is the accent ring. A word for each of
   those was the whole card. */
/* `hideHeader` is for the phone shell only: the Forces sheet is titled "Forces" and subtitled
   with this very count (from `readiness`, the same rule this panel reads), so the panel's own
   header repeated both two lines below them. The desktop docks, where the panel is the only thing
   naming itself, pass nothing and are untouched. */
const PlayerStatuses = ({ game, userId, hideHeader = false, replay }) => {
  const { t } = useTranslation()
  /* A replay has no round to wait for and no sockets to be offline on. The roster is the same
     panel either way — who holds what, how many crowns — but readiness and presence are claims
     about *now*, and in a replay they are answers to a question nobody asked: "0 / 2 ready" and a
     dimmed card beside two players who finished the game weeks ago.

     The Game view says which it is (`replay`). `selectIsReviewing` is also true for a live
     spectator, who watches a round that is still waiting on somebody and asked to see who
     (owner, 2026-10-04); the selector is only the fallback for a caller that passes nothing. */
  const borrowedSeat = useSelector(selectIsReviewing)
  const reviewing = replay ?? borrowedSeat
  /* The plates are taller than the roster's share of the dock and the scrollbar is hidden, so the
     panel has to say for itself that there is more of it: `data-scroll` names the edge with
     something behind it and the stylesheet fades that edge. Absent when the roster fits, which is
     the whole point of measuring rather than painting a permanent fade — a cue on a list that
     cannot scroll is a cue that lies. The same hook the top bar's chip row uses. */
  const [scroller, scrollEdges] = useScrollEdges({ axis: "y" })
  /* What a class *does* comes from the same server config the simulator applies (`RACE_BONUSES`,
     `valid_units_per_race`), exactly as the lobby's cards and the shop's read it — so the roster
     cannot promise a bonus the game does not give. */
  const gameConfig = useSelector(selectGameConfig)
  const raceBonuses = gameConfig?.rules_config?.RACE_BONUSES || {}
  const unitsPerRace = gameConfig?.valid_units_per_race || {}
  /* **In survival the enemies are not seats, they are the weather** (SV16). A wave lands every
     ten rounds and by round sixty there are more bots in `game.players` than this dock was ever
     built to draw — four plates to a row, six at the outside. So the people keep their cards and
     the bots become one strip: how many are still standing, out of how many have come, and how
     hard they play. The classic roster is untouched, where a seated bot is somebody's chosen
     opponent and belongs on the card its class wears. */
  const survival = isSurvival(game.mode)
  // A bot is an enemy of *this board* once it is in the snapshot's own `users`: the seat list is
  // the game's whole roster, and in a replay the opening step read "4 alive / 4 total" over two
  // enemies that had not landed yet. Live, the two agree — a wave's rows and its metadata land
  // in one payload.
  const landed = game.metadata?.users || {}
  const enemies = survival
    ? game.players.filter((seat) => seat.user.isBot && String(seat.user.id) in landed)
    : []
  const players = survival ? game.players.filter((seat) => !seat.user.isBot) : game.players
  // Elimination is read off the crowns, the same way the card below decides whether to dim itself,
  // so a replay and a live game agree.
  const crownsForCount = crownStandings(game.metadata, game.players)
  /* Readiness is only ever asked of players still in the game, and in survival only of the people:
     an eliminated player never readies again (the server's round gate skips them, R2) and the waves
     ready themselves the moment they land. Both rules live in `utils/crowns.readiness`, because the
     phone shell shows this same count on its Forces tab — and while each did its own arithmetic, a
     solo survival game read "0 / 1" here and "3/4" there. */
  const { ready: readyCount, total: stillPlayingCount } = readiness(game)

  // Base count per owner, read straight from the cell map's `owner` (a user id) rather than the
  // fog-scoped `metadata_by_user`, which is only ever populated for the requesting player.
  const baseCountByOwner = {}
  Object.values(game.metadata?.cells || {}).forEach((cell) => {
    if (cell?.owner == null) return
    baseCountByOwner[cell.owner] = (baseCountByOwner[cell.owner] || 0) + 1
  })

  // The crowns: how much of each player's win condition still stands. One 👑 per initial city,
  // dimmed once lost — a player at zero is eliminated, and the card says so by going dark.
  const crowns = crownsForCount

  /* **Every seat on one row, up to four** (owner, 2026-09-10). The roster is the panel you read to
     ask "who is in this game", and an answer that needs scrolling is half an answer. So the column
     count is the *cast*, not the width: four seats are four columns, and a 3v3 — the largest game
     there is — is two rows of three rather than one row of six 80px slivers. Written as a custom
     property because it is the one thing about this grid that only the data knows. */
  const seatColumns = players.length <= 4 ? Math.max(players.length, 1) : Math.ceil(players.length / 2)

  // The strip's two numbers, read the same way a card's dark crowns are: a bot with no crown left
  // standing is out of the game, whatever else it still holds.
  const enemiesAlive = enemies.filter((seat) => {
    const crown = crownsForCount[String(seat.user.id)]
    return crown == null || crown.held > 0
  }).length
  const enemyDifficulty = game.survivalDifficulty || enemies[0]?.botDifficulty || null

  return (
    <div className="statuses-container" ref={scroller} data-scroll={scrollEdges || undefined}>
      {!hideHeader && (
        <div className="statuses-header">
          <h3>{t("game.forces.title")}</h3>
          {!reviewing && (
            <span className="statuses-ready-count">
              {t("game.forces.readyCount", { ready: readyCount, total: stillPlayingCount })}
            </span>
          )}
        </div>
      )}
      {/* The waves, in one row. Its difficulty is the game's — every wave bot plays at the one
          the host set — read off the game row rather than off whichever bot happens to be first,
          which would be a second copy of a number the server publishes. Falling back to the
          roster's own difficulty keeps the strip honest against a payload that predates the
          column. */}
      {survival && enemies.length > 0 && (
        <div className="enemy-strip">
          <span className="enemy-strip-label">{t("game.forces.enemies")}</span>
          <span className="enemy-strip-count">
            {t("game.forces.enemiesAlive", { alive: enemiesAlive, total: enemies.length })}
          </span>
          {enemyDifficulty && (
            <span className={`enemy-strip-difficulty player-bot-${enemyDifficulty}`}>
              {t(`game.forces.difficulty_${enemyDifficulty}`)}
            </span>
          )}
        </div>
      )}
      <ul className="player-list" style={{ "--seat-columns": seatColumns }}>
        {players.map((p) => {
          const isSelf = p.user.id === userId
          const isHost = p.user.id === game.host?.id
          const baseCount = baseCountByOwner[p.user.id] || 0
          const crown = crowns[String(p.user.id)]
          const isEliminated = crown != null && crown.held === 0
          /* The picture is the door (owner, 2026-09-10: "if pressed, the helper of the class
             appears"). A class wears the plate of the unit only it fields, so the showcase is
             that unit's — its name, role and ability — with the class in the kicker and what the
             class does as the lines: the same screen the shop's cards open, so there is one
             answer to "what is a Technologist" and not two that can drift. */
          const unit = specialUnitFor(p.race, p.team, unitsPerRace) || raceUnits[p.race]
          const unitStats = gameConfig?.[`${p.team}_config`]?.TROOPS?.[unit]
          const bonuses = Object.entries(raceBonuses[p.race] || {})
          const openClassHelp = () =>
            ModalService.show("ArtShowcaseModal", {
              src: races[p.race],
              title: unit ? unitName(unit) : raceNames[p.race],
              kicker: `${raceNames[p.race] || p.race} · ${t(`game.battle.${p.team}`)}`,
              subtitle: unitStats ? zoneNames(t, gameConfig, p.team, unit) : undefined,
              lines: [
                unit ? t(`game.units.abilities.${unit}`, { defaultValue: "" }) : "",
                ...(bonuses.length
                  ? bonuses.map(([key, value]) => describeBonus(key, value, p.team))
                  : [t("shop.noBonuses")]),
              ],
              team: p.team,
            })

          return (
            <li
              key={p.user.id}
              className={`player-item ${p.team}${isSelf ? " self" : ""}${
                isEliminated ? " eliminated" : ""
              }`}
            >
              <button
                type="button"
                className="art-zoomable player-portrait-button"
                onClick={openClassHelp}
                title={raceNames[p.race] || p.race}
                aria-label={raceNames[p.race] || p.race}
              >
                <img src={races[p.race]} alt="" className="player-portrait" />
              </button>

              {/* A bot is named by how hard it plays, not by the word "bot" — three easy bots and
                  three impossible ones read identically otherwise, and which one is across the
                  table is the thing a player wants to know. Its own tone, so the ladder reads as
                  a dial. Top left of the plate (owner, 2026-09-10), where every plate has sky or
                  wall behind it and nothing below the picture is left to make a row of.

                  **The word "bot" is not on it** (owner, 2026-09-10) — `game.forces.difficulty_*`
                  rather than the lobby's `botDifficulty_*`, which keep theirs: there the tag sits
                  in a row of seats that are mostly people and it is what says this one is not,
                  where here it is the only thing that ever appears in this corner. */}
              {p.user.isBot && (
                <p className={`player-bot player-bot-${p.botDifficulty || "easy"}`}>
                  {t(`game.forces.difficulty_${p.botDifficulty || "easy"}`)}
                </p>
              )}

              {/* The caption: a lower third on a scrim of its own — the showcase's own layout,
                  which is the screen this picture is a door to. It does not take the press:
                  `pointer-events: none` leaves the whole plate one button. */}
              <div className="player-caption">
                <p className="player-name">
                  <span className="player-name-text">{p.user.username}</span>
                  {isHost && (
                    // Wrapped rather than classed: `Icon` takes a size, a title and its
                    // children, and nothing else — a className handed to it is quietly dropped.
                    <span className="player-host-mark" title={t("game.forces.host")}>
                      <HostIcon size={12} title={t("game.forces.host")} />
                    </span>
                  )}
                </p>

                {/* The win condition and the empire, in that order and **a row each** (owner,
                    2026-09-10): the crowns are what ends the game and the bases are how big it
                    got getting there. Two lines rather than one, which is what lets both be set
                    at a size worth reading on a card four to a row. A player with no crowns of
                    their own — nothing yet has one — still gets the bases. */}
                {crown && crown.total > 0 ? (
                  <p
                    className="player-crowns"
                    title={t("game.crowns.standing", { held: crown.held, total: crown.total })}
                    aria-label={t("game.crowns.standing", { held: crown.held, total: crown.total })}
                  >
                    {Array.from({ length: crown.total }).map((_, index) => (
                      <span key={index} className={`player-crown${index < crown.held ? "" : " lost"}`}>
                        👑
                      </span>
                    ))}
                  </p>
                ) : null}
                <p className="player-bases">{t("game.forces.bases", { count: baseCount })}</p>
              </div>

              {/* The icon this seat's cities and columns wear on the map, in a small frame in the
                  bottom-right corner (owner, 2026-10-09): what ties the card to the colour on the
                  board, which in a team game is the only way to tell two allies' ground apart. */}
              <span className="player-map-icon" title={t("lobby.mapIcon")}>
                <img src={unitIcons[seatIconFor(p)]} alt="" />
              </span>

              {/* An eliminated player is neither ready nor waited on: they are out. The dot used
                  to say "ESPERANDO…" beside a bot with nothing left on the map, which reads as a
                  round being held up by somebody who cannot act. Two dots in the card's corner
                  rather than a word (owner, 2026-09-09: two seats to a row) — the state is not
                  lost, since the dot is titled and the header counts it for the whole room. */}
              {reviewing || isEliminated ? null : (
                <span
                  className={`ready-dot${p.ready ? "" : " waiting"}`}
                  title={t(p.ready ? "game.forces.ready" : "game.forces.waiting")}
                  aria-label={t(p.ready ? "game.forces.ready" : "game.forces.waiting")}
                >
                  {p.ready ? "●" : "○"}
                </span>
              )}
            </li>
          )
        })}
      </ul>
    </div>
  )
}

export default PlayerStatuses
